import { Text, VStack } from 'ui_2';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { RaffleV2, RaffleV2Type } from 'jigsaw';
import PuzzleWalletSponsorSplash from '@assets/sponsors/puzzle_wallet/splash_bg.png';
import { GiveawayTimeRemaining } from '@src/components/giveaway/GiveawayTimeRemaining';
import { GiveawayCheckout } from '@src/components/giveaway/GiveawayCheckout';
import { ProductRedemptionInstructions } from '@src/components/store/ProductRedemptionInstructions';
import { ProductTermsAndConditions } from '@src/components/store/ProductTermsAndConditions';
import { useMemo } from 'react';
import { ProductAvailability } from '@src/components/store/ProductAvailability';
import { useDisplayFlag } from '@src/hooks/useArcadeStore';
import { HowGiveawaysWork } from '@src/components/giveaway/HowGiveawaysWork';
import { ViewProductDetails } from '@src/components/store/ViewProductDetails';
import { useGiveawayUtils } from '@src/hooks/useGiveaways';
import { GiveawayClosed } from '@src/components/giveaway/GiveawayClosed';

dayjs.extend(LocalizedFormat);

type Props = {
  giveaway: RaffleV2;
};

const Buy = ({ giveaway }: Props) => {
  const { isGiveawayClosed, isGiveawayFull } = useGiveawayUtils(giveaway);

  const img = useMemo(() => {
    if (
      giveaway.type === RaffleV2Type.product ||
      (giveaway.type === RaffleV2Type.eth &&
        (giveaway.productSku.bigImageURL ??
          giveaway.productSku.service.bigLogoImageURL))
    ) {
      return (
        giveaway.productSku.bigImageURL ??
        giveaway.productSku.service.bigLogoImageURL
      );
    } else if (giveaway?.sponsor.name === 'Puzzle Wallet') {
      return PuzzleWalletSponsorSplash;
    }
    return giveaway?.sponsor.splashPageBackgroundImageExtensionURL;
  }, [JSON.stringify(giveaway)]);

  const url =
    giveaway.type === RaffleV2Type.product
      ? giveaway.productSku.url ?? giveaway.productSku.service.url
      : undefined;
  const availability =
    giveaway.type === RaffleV2Type.product
      ? giveaway.productSku.availability ??
        giveaway.productSku.service.availability
      : undefined;
  const redemptionInstructions =
    giveaway.type === RaffleV2Type.product
      ? giveaway.productSku.redemptionInstructions ??
        giveaway.productSku.service.redemptionInstructions
      : undefined;
  const termsAndConditions =
    giveaway.type === RaffleV2Type.product
      ? giveaway.productSku.termsAndConditions ??
        giveaway.productSku.service.termsAndConditions
      : undefined;

  const displayFlag = useDisplayFlag({
    sku:
      giveaway.type === RaffleV2Type.product ? giveaway.productSku : undefined,
  });

  return (
    <VStack className='w-full items-center gap-4'>
      <div className='flex h-auto w-full flex-col items-center justify-center gap-4 md:flex-row md:items-start md:gap-12 lg:gap-20'>
        <VStack className='w-full max-w-[500px] items-center gap-4 md:w-[55%]'>
          <img
            src={img}
            className='aspect-[2] max-w-[calc(100%,400px)] rounded-lg object-cover object-center'
          />
          <VStack className='w-full gap-2'>
            <GiveawayTimeRemaining giveaway={giveaway} />
            <Text>
              {giveaway.winnerCount}{' '}
              {giveaway.winnerCount > 1 ? 'winners receive' : 'winner receives'}
            </Text>
            <Text font='medium-lg'>{giveaway.prizeDescription}</Text>
          </VStack>
          <VStack className='hidden w-full gap-2 md:flex'>
            {url && <ViewProductDetails url={url} />}
            {availability && (
              <ProductAvailability
                availability={availability}
                displayFlag={displayFlag}
              />
            )}
            <VStack>
              {redemptionInstructions && (
                <ProductRedemptionInstructions
                  redemptionInstructions={redemptionInstructions}
                />
              )}
              {termsAndConditions && (
                <ProductTermsAndConditions
                  termsAndConditions={termsAndConditions}
                />
              )}
              <HowGiveawaysWork type={giveaway.type} />
            </VStack>
          </VStack>
        </VStack>

        <div className='w-full max-w-[500px] gap-3 md:w-[45%]'>
          {isGiveawayClosed || isGiveawayFull ? (
            <GiveawayClosed giveaway={giveaway} />
          ) : (
            <GiveawayCheckout giveaway={giveaway} />
          )}
        </div>
        <VStack className='flex w-full max-w-[500px] gap-2 md:hidden'>
          {url && <ViewProductDetails url={url} />}
          {availability && (
            <ProductAvailability
              availability={availability}
              displayFlag={displayFlag}
            />
          )}
          <VStack>
            {redemptionInstructions && (
              <ProductRedemptionInstructions
                redemptionInstructions={redemptionInstructions}
              />
            )}
            {termsAndConditions && (
              <ProductTermsAndConditions
                termsAndConditions={termsAndConditions}
              />
            )}
            <HowGiveawaysWork type={giveaway.type} />
          </VStack>
        </VStack>
      </div>
    </VStack>
  );
};

export default Buy;
